import { Body, Controller, Get, Param, Patch, Post, Req, UseGuards } from '@nestjs/common';
import {
  ApiBearerAuth,
  ApiBody,
  ApiOperation,
  ApiParam,
  ApiResponse,
  ApiSecurity,
  ApiTags,
} from '@nestjs/swagger';
import { OrderListResponse, OrderSummary, UpdateOrderStatusRequest } from '@cart/shared-types';
import { AdminAccessGuard } from '../auth/admin-access.guard';
import { GuardedRequest, AuthGuard } from '../auth/auth.guard';
import { OrdersService } from './orders.service';

@ApiTags('Orders')
@ApiBearerAuth()
@ApiSecurity('store-id')
@UseGuards(AuthGuard)
@Controller('orders')
export class OrdersController {
  constructor(private readonly ordersService: OrdersService) {}

  @Post()
  @ApiOperation({ summary: 'Create an order from the current cart' })
  @ApiResponse({ status: 201, description: 'Order created' })
  createFromCart(@Req() request: GuardedRequest): Promise<OrderSummary> {
    return this.ordersService.createFromCart(request.user.id);
  }

  @Get()
  @ApiOperation({ summary: 'List orders for the current user' })
  @ApiResponse({ status: 200, description: 'Returns order list for the authenticated user' })
  list(@Req() request: GuardedRequest): Promise<OrderListResponse> {
    return this.ordersService.list(request.user.id);
  }

  @Get(':id')
  @ApiOperation({ summary: 'Get order detail by id' })
  @ApiParam({ name: 'id', type: String })
  @ApiResponse({ status: 200, description: 'Returns order with items and payment status' })
  @ApiResponse({ status: 404, description: 'Order not found' })
  detail(
    @Req() request: GuardedRequest,
    @Param('id') orderId: string
  ): Promise<OrderSummary> {
    return this.ordersService.detail(orderId, request.user.id);
  }
}

@ApiTags('Admin · Orders')
@ApiBearerAuth()
@UseGuards(AuthGuard, AdminAccessGuard)
@Controller('admin/orders')
export class AdminOrdersController {
  constructor(private readonly ordersService: OrdersService) {}

  @Get()
  @ApiOperation({ summary: 'List all orders across users (admin)' })
  @ApiResponse({ status: 200, description: 'Returns all orders' })
  list(): Promise<OrderListResponse> {
    return this.ordersService.list();
  }

  @Get(':id')
  @ApiOperation({ summary: 'Get order detail (admin)' })
  @ApiParam({ name: 'id', type: String })
  @ApiResponse({ status: 200, description: 'Returns order with items, customer, and status history' })
  @ApiResponse({ status: 404, description: 'Order not found' })
  adminDetail(@Param('id') orderId: string): Promise<OrderSummary> {
    return this.ordersService.detail(orderId);
  }

  @Patch(':id/status')
  @ApiOperation({ summary: 'Update order fulfillment status (admin)' })
  @ApiParam({ name: 'id', type: String })
  @ApiBody({
    schema: {
      type: 'object',
      required: ['status'],
      properties: {
        status: {
          type: 'string',
          enum: ['placed', 'processing', 'shipped', 'out_for_delivery', 'delivered', 'cancelled'],
          example: 'shipped',
        },
      },
    },
  })
  @ApiResponse({ status: 200, description: 'Order status updated' })
  @ApiResponse({ status: 404, description: 'Order not found' })
  updateStatus(
    @Param('id') orderId: string,
    @Body() body: UpdateOrderStatusRequest
  ): Promise<OrderSummary> {
    return this.ordersService.updateStatus(orderId, body.status);
  }
}
